import { ChevronDownIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import { message } from 'antd'
import { useRolodexEdit } from 'features/fde'
import { useKestrel } from 'features/kestrelViewer'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import React, { useState } from 'react'
import { KVPrimitive, RolodexEntryPatchJson } from 'types'

import { NDEDataItem } from './NdeDataItem'
import {
  Button,
  Content,
  Field,
  Header,
  Section,
} from './ndeDataSection.styles'
import { NDEUploadForm } from './NdeUploadForm'

interface Props {
  selectedPrimitive: KVPrimitive
}

export const NDEDataSection: React.FC<Props> = ({ selectedPrimitive }) => {
  const [isOpen, setIsOpen] = useState(false)

  const { kestrelModel, primitives, setPrimitives, sendKVCommand } =
    useKestrel()
  const { setSelectedMarkup } = useKestrelViewerContext()
  const { rolodexPatch } = useRolodexEdit()

  const handleSubmit = async (dataType: string, parsedData: number[][]) => {
    if (!primitives || !kestrelModel?.id) {
      message.error('Missing required data')
      return
    }

    const updatedPrimitives = primitives.map((p) => {
      if (p.id === selectedPrimitive.id) {
        const updated = Object.assign(
          Object.create(Object.getPrototypeOf(p)),
          p,
          {
            ndeData: { ...(p.ndeData || {}), [dataType]: parsedData },
          }
        )
        setSelectedMarkup(updated)
        return updated
      }
      return p
    })

    setPrimitives(updatedPrimitives)

    const patchData: RolodexEntryPatchJson = {
      id: kestrelModel.id,
      data: { primitives: updatedPrimitives.map((p) => KVPrimitive.toJSON(p)) },
    }

    try {
      await rolodexPatch([patchData])

      if (sendKVCommand) {
        sendKVCommand({
          command: KestrelCommands.LOAD_NDE_DATA,
          primitiveId: selectedPrimitive.id,
          ndeDataType: dataType,
          data: parsedData,
        })
      }

      message.success('NDE data uploaded')
    } catch (error) {
      console.error('Failed to save NDE data:', error)
      message.error('Failed to save NDE data')
    }
  }

  const handleDelete = async (dataType: string) => {
    if (!primitives || !kestrelModel?.id) return

    const updatedPrimitives = primitives.map((p) => {
      if (p.id === selectedPrimitive.id && p.ndeData) {
        const newNdeData = { ...p.ndeData }
        delete newNdeData[dataType]
        const updated = Object.assign(
          Object.create(Object.getPrototypeOf(p)),
          p,
          {
            ndeData:
              Object.keys(newNdeData).length > 0 ? newNdeData : undefined,
          }
        )
        setSelectedMarkup(updated)
        return updated
      }
      return p
    })

    setPrimitives(updatedPrimitives)

    const patchData: RolodexEntryPatchJson = {
      id: kestrelModel.id,
      data: { primitives: updatedPrimitives.map((p) => KVPrimitive.toJSON(p)) },
    }

    try {
      await rolodexPatch([patchData])

      if (sendKVCommand) {
        sendKVCommand({
          command: KestrelCommands.DELETE_NDE_DATA_LAYER,
          primitiveId: selectedPrimitive.id,
          ndeDataType: dataType,
        })
      }

      message.success('NDE data deleted')
    } catch (error) {
      console.error('Failed to delete NDE data:', error)
      message.error('Failed to delete NDE data')
    }
  }

  const handleDisplay = (dataType: string) => {
    if (sendKVCommand) {
      sendKVCommand({
        command: KestrelCommands.TOGGLE_NDE_DATA_LAYER,
        ndeDataType: dataType,
      })
    }
  }

  const handleHideAll = () => {
    if (sendKVCommand) {
      sendKVCommand({
        command: KestrelCommands.CLEAR_NDE_DATA_LAYER,
      })
    }
  }

  return (
    <Section>
      <Header onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? (
          <ChevronDownIcon width={16} height={16} />
        ) : (
          <ChevronRightIcon width={16} height={16} />
        )}
        <span>NDE Data</span>
      </Header>

      <Content $isOpen={isOpen}>
        {selectedPrimitive.ndeData &&
          Object.keys(selectedPrimitive.ndeData).length > 0 && (
            <Field>
              <Button $variant='default' onClick={handleHideAll}>
                Hide NDE Data
              </Button>
            </Field>
          )}

        {selectedPrimitive.ndeData &&
          Object.entries(selectedPrimitive.ndeData).map(([type, data]) => (
            <NDEDataItem
              key={type}
              type={type}
              data={data}
              primitiveId={selectedPrimitive.id}
              onDisplay={handleDisplay}
              onDelete={handleDelete}
              sendKVCommand={sendKVCommand}
            />
          ))}

        <NDEUploadForm onSubmit={handleSubmit} />
      </Content>
    </Section>
  )
}
